import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DeviceTypeData } from '../../shared/models/device-type-data.model';
import {
  Device,
  LogisticStatusValues,
  UpdateDeviceLogisticResponse,
} from '../../shared/models/device.model';
import { animate, style, trigger, transition } from '@angular/animations';
import {
  DeviceTypeKeys,
  DeviceTypes,
} from '../../shared/models/device-types-attributes';
import {
  DeviceCardBaseItem,
  DeviceListItem,
  getCustomerId,
  getCustomerName,
  getDeviceId,
  getExternalDeviceId,
  DeviceStatus,
  DeviceStatusKeys,
  DeviceStatusData,
} from '../../shared/models/device-list.model';
import { FormControl, FormGroup } from '@angular/forms';
import { DeviceService } from '../../shared/services/device.service';
import { Observable, map, startWith } from 'rxjs';
import { downloadPdfFromBase64 } from "../../../shared/utils/pdf";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: 'app-device-basic',
  templateUrl: './device-basic.component.html',
  styleUrls: ['./device-basic.component.scss'],
  animations: [
    trigger('appear', [
      transition('* <=> *', [
        animate('0ms', style({ transform: 'translateX(-100%)' })),
        animate('500ms ease-out', style({ transform: 'translateX(0%)' })),
      ]),
    ]),
    trigger('shrink', [
      transition(':enter', [
        animate('0ms ease-in-out', style({ height: '0px' })),
        animate('500ms ease-in-out', style({ height: '*' })),
      ]),
      transition(':leave', [
        animate('0ms ease-in-out', style({ height: '*' })),
        animate('500ms ease-in-out', style({ height: '0px' })),
      ]),
    ]),
  ],
})
export class DeviceBasicComponent {
  @Input() device: Device | undefined;
  @Input() deviceTypeData: DeviceTypeData | undefined;
  @Input() itemData!: DeviceListItem;
  @Output() redirectEvent = new EventEmitter<DeviceTypeKeys>();
  @Output() monitoringEvent = new EventEmitter();
  @Output() editEventEmitter = new EventEmitter<UpdateDeviceLogisticResponse>();

  protected expanded: boolean = true;

  protected loading = false;
  protected modify: boolean = false;
  protected readonly editForm = new FormGroup({
    logisticStatus: new FormControl('', { nonNullable: true }),
    logisticStatusDesc: new FormControl('', { nonNullable: true }),
  });

  protected logValuesObservable: Observable<string[]>;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private deviceService: DeviceService,
    private _snackBar: MatSnackBar,
    protected translate: TranslateService,
  ) {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        if (result.matches) {
          this.expanded = false;
        }
      },
    });
    this.logValuesObservable = this.editForm
      .get('logisticStatus')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => (value ? this.filterLogValues(value) : this.LogValues))
      )!;
  }

  /*átmeneti a bemutató kedvéért*/
  public getRandomNumber(): number {
    return Math.floor(Math.random() * 100) + 1;
  }

  protected selectId(type: DeviceTypeKeys) {
    this.redirectEvent.emit(type);
  }

  protected checkoutStatus() {
    this.monitoringEvent.emit();
  }

  private getCardItemByType(type: DeviceTypeKeys) {
    const data = {
      ...this.itemData,
      externalDeviceTypeCode: type,
    };
    return {
      externalDeviceTypeCode: type,
      deviceId: getDeviceId(data),
      externalDeviceId: getExternalDeviceId(data),
      customerId: getCustomerId(data),
      customerName: getCustomerName(data),
    };
  }

  protected get linkItems(): DeviceCardBaseItem[] {
    const types = [this.Keys.SZT, this.Keys.GATE, this.Keys.SIMCARD];
    const index = types.indexOf(this.itemData.externalDeviceTypeCode);
    [types[0], types[index]] = [types[index], types[0]];
    return types
      .map((type) => this.getCardItemByType(type))
      .filter((i) => i.externalDeviceId);
  }

  protected get isStatus(): boolean {
    const status = this.device?.status as DeviceStatus;
    return Boolean(status && DeviceStatusKeys.includes(status));
  }

  protected get statusIcon(): string {
    const status = this.device?.status as DeviceStatus;
    return DeviceStatusData[status].icon;
  }

  protected toggleModify() {
    if (!this.modify) {
      this.editForm.patchValue({
        logisticStatus: this.device?.logisticStatus,
        logisticStatusDesc: this.device?.logisticStatusDesc,
      });
    }
    this.modify = !this.modify;
  }

  protected saveLogistic() {
    const needToSave = Object.keys(this.editForm.controls).some(
      (key) =>
        this.editForm.get(key)?.value !== this.device?.[key as keyof Device]
    );
    if (!needToSave) {
      this.modify = false;
      return;
    }
    this.loading = true;
    const { logisticStatus, logisticStatusDesc } = this.editForm.value;
    this.deviceService
      .updateLogisticStatus({
        deviceId: this.device!.deviceId,
        externalDeviceId: this.device!.externalDeviceId,
        logisticStatus,
        logisticStatusDesc,
      })
      .subscribe({
        next: (res) => {
          this.device!.logisticStatus = res.logisticStatus;
          this.device!.logisticStatusDesc = res.logisticStatusDesc;
          this.device!.lastUpdatedTimestamp = res.lastUpdatedTimestamp;
          this.editEventEmitter.emit(res);
        },
      })
      .add(() => {
        this.loading = false;
        this.modify = false;
      });
  }

  protected isDownloading = false;
  protected downloadDatasheet(event: Event): void {

    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;
    event.stopPropagation();
    this.deviceService
      .getDeviceDatasheetLinkInstantly(this.device!.externalDeviceId)
      .subscribe({
        next: (res) => {
          downloadPdfFromBase64(res.encodedPdf, this.device!.externalDeviceId, true)
          this._snackBar.open(this.translate.instant('datasheet-success'), '', {duration: 2000});
        },
        error: () => {
          this._snackBar.open(this.translate.instant('datasheet-error'), '', {duration: 2000});
        },
        complete: () => {
          this.isDownloading = false;
        }
      })
  }

  protected readonly Keys = DeviceTypeKeys;
  protected readonly Types = DeviceTypes;
  protected readonly LogValues = LogisticStatusValues;

  protected get showLogistic(): boolean {
    return (
      !this.modify &&
      Boolean(
        this.canEdit ||
          this.device?.logisticStatus ||
          this.device?.logisticStatusDesc
      )
    );
  }

  protected get typeCode() {
    return this.Keys[this.itemData.externalDeviceTypeCode];
  }

  private filterLogValues(value: string): string[] {
    const lowerValue = value.toLowerCase();
    return this.LogValues.filter(
      (item) => value && item.toLowerCase().includes(lowerValue)
    );
  }

  protected clearFilter(name: string, $event: MouseEvent) {
    $event.stopPropagation();
    this.editForm.get(name)?.setValue('');
  }

  protected emptyFilter(name: string): boolean {
    const item = this.editForm.get(name);
    return (item?.value ?? '') === '' || Boolean(item?.disabled);
  }

  protected get canEdit(): boolean {
    return (
      Boolean(this.device?.writeRight) &&
      this.itemData.externalDeviceTypeCode === this.Keys.SZT
    );
  }

  protected get deviceType(): DeviceTypeKeys {
    return this.itemData!.externalDeviceTypeCode;
  }
}
